/**
 * useNetworkHierarchy - Hook para consumir análisis de Jerarquía Morfológica
 *
 * Este hook gestiona la obtención de datos del análisis de jerarquía morfológica,
 * permitiendo cambiar el perfil y refrescándose automáticamente cuando cambia
 * el proyecto o el perfil seleccionado.
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { data, loading, error, profile, setProfile, refetch } = useNetworkHierarchy(projectId);
 *
 *   if (loading) return <div>Cargando...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   if (!data) return null;
 *
 *   return (
 *     <div>
 *       <select value={profile} onChange={e => setProfile(e.target.value)}>
 *         <option value="global">Global</option>
 *         <option value="local">Local</option>
 *         <option value="equilibrado">Equilibrado</option>
 *       </select>
 *       <p>Segmentos: {data.metadata.totalSegments}</p>
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  networkHierarchyApi,
  type NetworkHierarchyResponse,
  type HierarchyProfile,
} from '../services/network-hierarchy-api.service';

// ============================================================================
// TYPES
// ============================================================================

export interface UseNetworkHierarchyResult {
  /** Datos del análisis (null si no hay datos o aún no cargó) */
  data: NetworkHierarchyResponse | null;
  /** Si está cargando */
  loading: boolean;
  /** Mensaje de error si hay */
  error: string | null;
  /** Perfil actualmente seleccionado */
  profile: HierarchyProfile;
  /** Cambiar perfil (trigger refetch automático) */
  setProfile: (profile: HierarchyProfile) => void;
  /** Refrescar datos manualmente */
  refetch: () => Promise<void>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook para obtener análisis de jerarquía morfológica
 *
 * @param projectId - ID del proyecto activo (puede ser null si no hay proyecto)
 * @returns Estado del análisis y controles
 *
 * @remarks
 * - Se recarga automáticamente cuando cambia projectId o profile
 * - Usa ref para evitar race conditions en cambios rápidos de proyecto
 * - Retorna data=null si projectId es null
 */
export interface UseNetworkHierarchyOptions {
  /** Si true, dispara la carga automática. Default: true para retrocompatibilidad. */
  enabled?: boolean;
}

export function useNetworkHierarchy(
  projectId: string | null,
  { enabled = true }: UseNetworkHierarchyOptions = {},
): UseNetworkHierarchyResult {
  const [data, setData] = useState<NetworkHierarchyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<HierarchyProfile>('equilibrado');

  // Ref para evitar race conditions cuando el proyecto cambia
  const currentProjectIdRef = useRef<string | null>(null);

  /**
   * Carga datos del análisis
   */
  const loadAnalysis = useCallback(async () => {
    if (!projectId) {
      // Si no hay proyecto, resetear
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Guardar projectId de esta request para verificar después
    const requestProjectId = projectId;

    setLoading(true);
    setError(null);

    try {
      const result = await networkHierarchyApi.getAnalysis(projectId, profile);

      // Race condition check: verificar que el proyecto no cambió mientras esperábamos
      if (currentProjectIdRef.current !== requestProjectId) {
        console.log(
          '[useNetworkHierarchy] Ignoring stale response for project:',
          requestProjectId,
          '- current project is:',
          currentProjectIdRef.current,
        );
        return;
      }

      setData(result);
      setLoading(false);

      console.log(
        '[useNetworkHierarchy] Loaded hierarchy analysis:',
        result.metadata.totalSegments,
        'segments with profile',
        profile,
      );
    } catch (e: unknown) {
      // También verificar race condition en error
      if (currentProjectIdRef.current !== requestProjectId) {
        console.log('[useNetworkHierarchy] Ignoring error for stale project:', requestProjectId);
        return;
      }

      const errorMessage = e instanceof Error ? e.message : 'Unknown error loading hierarchy analysis';
      setError(errorMessage);
      setLoading(false);
      console.error('[useNetworkHierarchy] Error loading analysis:', errorMessage);
    }
  }, [projectId, profile]);

  // Efecto: cargar cuando cambia projectId, profile, o enabled
  // Si enabled=false, no dispara request (diferido hasta que el panel esté activo)
  useEffect(() => {
    // Actualizar ref ANTES de hacer la request (evita race conditions)
    currentProjectIdRef.current = projectId;

    if (!enabled) {
      return;
    }

    void loadAnalysis();
  }, [projectId, profile, loadAnalysis, enabled]);

  return {
    data,
    loading,
    error,
    profile,
    setProfile,
    refetch: loadAnalysis,
  };
}
